import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { CheckboxInput } from '@/components/form/fields/checkbox/Checkbox';
import SiChevronDown from '@/icons/SiChevronDown';
import SiChevronRight from '@/icons/SiChevronRight';
import { CustomerOrgTree } from '@/services/org/useBuildOrgTree';

import { FunctionComponent, useEffect, useMemo, useState } from 'react';

export interface ProcessedCustomerOrgTree extends CustomerOrgTree {
  checked: boolean;
}

interface CustomerSectionProps {
  customer: ProcessedCustomerOrgTree;
  onSelect: () => void;
}

const CustomerSection: FunctionComponent<CustomerSectionProps> = ({ customer, onSelect }) => {
  useEffect(() => {
    setChecked(customer.checked);
    onSelect();
  }, [customer.checked]);

  const [checked, setChecked] = useState(customer.checked);

  return (
    <div className="flex items-center">
      <CheckboxInput
        id={`${customer.customerId}-checkbox`}
        value={checked}
        onChange={() => {
          customer.checked = !customer.checked;
          setChecked(!checked);
        }}
        className="w-6 h-6 mr-4"
      />
      <div className="space-x-2 flex items-end">
        <p className="text-xl">{customer.customerName}</p>
      </div>
    </div>
  );
};

interface MspSectionProps {
  msp: ProcessedOrgTree;
}

const MspSection: FunctionComponent<MspSectionProps> = ({ msp }) => {
  const [expanded, setExpanded] = useState(false);

  const [mspChecked, setMspChecked] = useState(msp.checked);

  const [forceUpdate, setForceUpdate] = useState(0);

  const selectedCustomerLabel = useMemo(() => {
    let label = '';

    const checkedCustomers = msp.customers.filter(customer => customer.checked);

    if (checkedCustomers.length === msp.customers.length) {
      label = 'All Customers';
    } else if (checkedCustomers.length > 0) {
      label = `${checkedCustomers.length} Customer${checkedCustomers.length === 1 ? '' : 's'}`;
    }

    return label;
  }, [forceUpdate, mspChecked]);

  useEffect(() => {
    const checkedCustomers = msp.customers.filter(customer => customer.checked);

    if (checkedCustomers.length > 0 && !mspChecked) {
      onMspChecked(true, false);
    } else if (checkedCustomers.length === 0 && mspChecked) {
      onMspChecked(false, true);
    }
  }, [forceUpdate]);

  const triggerRerenderAfterCustomerUpdate = () => {
    setForceUpdate(prev => prev + 1);
  };

  const onMspChecked = (checked: boolean, selectAll?: boolean) => {
    msp.checked = checked;
    selectAll &&
      msp.customers.forEach(customer => {
        customer.checked = checked;
      });
    setMspChecked(checked);
  };

  return (
    <div>
      <div className="flex items-center">
        <Button
          variant={ButtonVariant.Ghost}
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          {expanded ? <SiChevronDown className="text-4xl" /> : <SiChevronRight className="text-4xl" />}
        </Button>
        <CheckboxInput
          id={`${msp.mspId}-checkbox`}
          value={mspChecked}
          onChange={() => {
            onMspChecked(!mspChecked, true);
          }}
          className="w-6 h-6 mr-4 ml-1"
        />
        <div className="space-x-2 flex items-end">
          <p className="text-xl">{msp.mspName}</p>
          {selectedCustomerLabel && <p className="text-gray-400">({selectedCustomerLabel})</p>}
        </div>
      </div>
      {expanded && (
        <div className="ml-24 space-y-4">
          {msp.customers.map(customer => (
            <CustomerSection
              key={customer.customerId}
              customer={customer as ProcessedCustomerOrgTree}
              // Need to trigger rerender of parent component to update labels. Easiest way I found is to force a rerender
              onSelect={triggerRerenderAfterCustomerUpdate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export interface ProcessedOrgTree {
  mspId: string;
  mspName: string;
  checked: boolean;
  customers: ProcessedCustomerOrgTree[];
}

interface OrgTreeProps {
  data: ProcessedOrgTree[];
}

const OrgTree: FunctionComponent<OrgTreeProps> = ({ data }) => {
  return (
    <div className="space-y-1 overflow-y-auto max-h-[36rem]">
      {data.map(msp => {
        return <MspSection key={msp.mspId} msp={msp} />;
      })}
    </div>
  );
};

export default OrgTree;
